import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAtom, useSetAtom } from "jotai";

import {
  createErrorResultFromException,
  IDateRange,
  IReceipt,
  receiptsAtom,
  receiptsCacheKeyAtom,
  receiptsErrorAtom,
  receiptsLoadingAtom,
} from "@prostocheck/shared";

import { fetchReceipts } from "../../api";
import { notifyError } from "../../notifications";
import { useSession } from "../use-session";
import { IUseReceipts } from "./use-receipts.models";

/**
 * Fetches a register's receipts over `range` and writes them into
 * `receiptsAtom` — nothing else. `range` comes from `useRegisterFilters`, the
 * form both this and `useZReports` are driven by. Results and a cache key
 * live in atoms (`receiptsAtom`, `receiptsCacheKeyAtom`) so a remount — e.g.
 * opening a receipt modal and closing it — skips the network call when the
 * cache key already matches.
 */
export const useReceipts = (range: IDateRange): IUseReceipts => {
  const { t } = useTranslation();
  const { session } = useSession();

  const setReceiptsList = useSetAtom(receiptsAtom);
  const [cacheKey, setCacheKey] = useAtom(receiptsCacheKeyAtom);
  const setIsLoading = useSetAtom(receiptsLoadingAtom);
  const setErrorResult = useSetAtom(receiptsErrorAtom);

  // The dates bound the request; time and type narrow what came back client-side.
  const getReceipts = useCallback(
    async (cashRegisterFn: string) => {
      const isCached =
        cacheKey?.cashRegisterFn === cashRegisterFn &&
        cacheKey?.from === range.from &&
        cacheKey?.to === range.to;

      if (isCached) {
        setErrorResult(null);

        return;
      }

      setIsLoading(true);
      setErrorResult(null);

      try {
        // `useSession` already guarantees a session inside RequireSession's routes.
        const result = await fetchReceipts({
          client: session!.client,
          cashRegisterFn,
          from: range.from,
          to: range.to,
        });

        if (!result.success) {
          setErrorResult(result);
          notifyError({ result, t });

          return;
        }

        setReceiptsList(result.data ?? []);
        setCacheKey({ cashRegisterFn, from: range.from, to: range.to });
      } catch (error) {
        const result = createErrorResultFromException<IReceipt[]>(error);

        setErrorResult(result);
        notifyError({ result, t });
      } finally {
        setIsLoading(false);
      }
    },
    [
      session,
      range.from,
      range.to,
      cacheKey,
      t,
      setIsLoading,
      setErrorResult,
      setReceiptsList,
      setCacheKey,
    ],
  );

  return { getReceipts };
};
